<script setup lang="ts">
import { computed } from 'vue'
import { buildReviewChartBars } from '../composables/productOwnerReviewCharts'
import { formatCents } from '../composables/formatCents'
import type { ProductOwnerReviewDay } from '../stores/product_owner_runs'

// Daily spend / PRs-merged bars for the Review tab (#1192) — two
// single-series charts, never one dual-axis chart (spend and PR count are
// different scales). Plain div-height bars, no chart library, same spirit
// as ProductOwnerActivityHeatmap.vue's own div-grid cells — deliberately
// NOT the daily idle chart the issue excludes on purpose (that's the
// Activity tab's heatmap and the MCP `trend` output's job, not this one's).
const props = defineProps<{
  days: ProductOwnerReviewDay[]
}>()

const spendBars = computed(() => buildReviewChartBars(props.days, (d) => d.spend_cents))
const mergedBars = computed(() => buildReviewChartBars(props.days, (d) => d.merged_pr_count))
</script>

<template>
  <div class="flex flex-col gap-4">
    <div class="flex flex-col gap-1" data-testid="product-owner-review-spend-chart">
      <span class="text-xs text-white/40">Spend per day</span>
      <div class="flex items-end gap-[3px] h-20 overflow-x-auto pb-1">
        <div
          v-for="bar in spendBars"
          :key="bar.date"
          class="w-3 min-w-[0.75rem] rounded-sm bg-sky-500/60"
          :style="{ height: `${Math.max(bar.heightPercent, 2)}%` }"
          :title="`${bar.date}: ${formatCents(bar.value)}`"
          :data-testid="`product-owner-review-spend-bar-${bar.date}`"
        />
      </div>
    </div>

    <div class="flex flex-col gap-1" data-testid="product-owner-review-merged-chart">
      <span class="text-xs text-white/40">PRs merged per day</span>
      <div class="flex items-end gap-[3px] h-20 overflow-x-auto pb-1">
        <div
          v-for="bar in mergedBars"
          :key="bar.date"
          class="w-3 min-w-[0.75rem] rounded-sm bg-emerald-500/60"
          :style="{ height: `${Math.max(bar.heightPercent, 2)}%` }"
          :title="`${bar.date}: ${bar.value} merged`"
          :data-testid="`product-owner-review-merged-bar-${bar.date}`"
        />
      </div>
    </div>
  </div>
</template>
